import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, ChevronRight, Building2, Calendar } from 'lucide-react';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { useIsMobile } from '@/hooks/use-mobile';

interface SellerProfile {
  id?: string;
  user_id?: string;
  display_name?: string | null;
  avatar_url?: string | null;
  company_name?: string | null;
  company_logo_url?: string | null;
  represent_as_company?: boolean | null;
  user_type?: string | null;
  is_verified?: boolean | null;
  created_at?: string;
  dealer_status?: string | null;
}

interface SellerSectionProps {
  profile: SellerProfile | null;
  className?: string;
  compact?: boolean;
  onNavigate?: () => void;
}

export const SellerSection: React.FC<SellerSectionProps> = ({
  profile,
  className,
  compact = false,
  onNavigate,
}) => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  
  if (!profile) return null;

  // Use user_id for navigation (this is what we join on in profiles table)
  const userId = profile.user_id || profile.id;
  if (!userId) return null;

  // Determine display based on company representation setting
  const showAsCompany = profile.represent_as_company && profile.company_name;
  const displayName = showAsCompany 
    ? profile.company_name 
    : (profile.display_name || profile.company_name || 'Anonymous Seller');
  const avatarUrl = showAsCompany 
    ? profile.company_logo_url 
    : profile.avatar_url;
  const initials = (displayName || 'A').charAt(0).toUpperCase();
  const isDealer = profile.user_type === 'dealer' || profile.dealer_status === 'approved';
  const memberSince = profile.created_at 
    ? format(new Date(profile.created_at), 'MMM yyyy')
    : null;

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    
    const targetPath = `/user/${userId}`;
    
    // Close modal first if callback provided
    if (onNavigate) {
      onNavigate();
    }
    
    // Defer navigation to next frame to ensure it completes
    // before drawer unmounting interferes on mobile
    requestAnimationFrame(() => {
      navigate(targetPath);
    });
  };

  if (compact) {
    return (
      <div 
        className={cn(
          "flex items-center gap-2.5 group cursor-pointer",
          className
        )}
        onClick={handleClick}
      >
        {showAsCompany && avatarUrl ? (
          <div className="h-8 px-1.5 flex items-center justify-center bg-white dark:bg-slate-800 rounded-lg border border-border/50 shadow-sm">
            <img 
              src={avatarUrl} 
              alt={displayName || 'Company'} 
              className="h-5 w-auto max-w-[60px] object-contain"
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
            />
          </div>
        ) : (
          <Avatar className="h-8 w-8 border border-border/50 shadow-sm">
            <AvatarImage src={avatarUrl || ''} alt={displayName || ''} />
            <AvatarFallback className="bg-primary/10 text-primary text-xs font-semibold">
              {initials}
            </AvatarFallback>
          </Avatar>
        )}
        <span className="text-sm font-medium text-foreground group-hover:text-primary transition-colors">
          {displayName}
        </span>
        {profile.is_verified && (
          <CheckCircle className="w-3.5 h-3.5 text-green-500" />
        )}
      </div>
    );
  }

  // Mobile-specific non-compact layout - redesigned for better display
  if (isMobile) {
    return (
      <div 
        className={cn(
          "group block cursor-pointer",
          className
        )}
        onClick={handleClick}
      >
        <div className="p-4 rounded-xl bg-background border-2 border-border/50 shadow-sm hover:border-primary/30 hover:shadow-md transition-all duration-300">
          {/* Top row: Logo/Avatar + Name + Verified + Arrow */}
          <div className="flex items-center gap-3">
            {/* Avatar / Company Logo */}
            {showAsCompany && avatarUrl ? (
              <div className="h-14 min-w-[80px] px-3 flex items-center justify-center bg-background rounded-lg border border-border shadow-sm flex-shrink-0">
                <img 
                  src={avatarUrl} 
                  alt={displayName || 'Company'} 
                  className="h-10 w-auto max-w-[100px] object-contain"
                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                />
              </div>
            ) : (
              <Avatar className="h-14 w-14 border-2 border-background shadow-lg flex-shrink-0">
                <AvatarImage src={avatarUrl || ''} alt={displayName || ''} />
                <AvatarFallback className="bg-gradient-to-br from-primary to-primary/80 text-primary-foreground text-lg font-bold">
                  {initials}
                </AvatarFallback>
              </Avatar>
            )}
            
            {/* Name + Verified icon */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h4 className="font-semibold text-foreground text-base break-words">
                  {displayName}
                </h4>
                {profile.is_verified && (
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                )}
              </div>
              
              {/* Member since - inline on mobile */}
              {memberSince && (
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-1">
                  <Calendar className="w-3.5 h-3.5" />
                  <span>Member since {memberSince}</span>
                </div>
              )}
            </div>
            
            {/* Arrow */}
            <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all flex-shrink-0" />
          </div>
          
          {/* Badges row - properly spaced below */}
          {(showAsCompany || isDealer) && (
            <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-border/50">
              {showAsCompany && (
                <Badge variant="secondary" className="text-xs bg-primary/10 text-primary border-primary/20 px-2.5 py-1">
                  <Building2 className="w-3 h-3 mr-1.5" />
                  Company
                </Badge>
              )}
              {isDealer && (
                <Badge variant="secondary" className="text-xs bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/20 px-2.5 py-1">
                  <CheckCircle className="w-3 h-3 mr-1.5" />
                  Verified Dealer
                </Badge>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Desktop non-compact layout (unchanged)
  return (
    <div 
      className={cn(
        "group block cursor-pointer",
        className
      )}
      onClick={handleClick}
    >
      <div className="flex items-center gap-4 p-4 rounded-xl bg-gradient-to-br from-muted/50 to-muted/30 border border-border/50 hover:border-primary/30 hover:shadow-md transition-all duration-300">
        {/* Avatar / Company Logo */}
        {showAsCompany && avatarUrl ? (
          <div className="h-14 px-3 flex items-center justify-center bg-background rounded-lg border-2 border-background shadow-lg ring-2 ring-primary/10 group-hover:ring-primary/30 transition-all">
            <img 
              src={avatarUrl} 
              alt={displayName || 'Company'} 
              className="h-10 w-auto max-w-[100px] object-contain"
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
            />
          </div>
        ) : (
          <Avatar className="h-14 w-14 border-2 border-background shadow-lg ring-2 ring-primary/10 group-hover:ring-primary/30 transition-all">
            <AvatarImage src={avatarUrl || ''} alt={displayName || ''} />
            <AvatarFallback className="bg-gradient-to-br from-primary to-primary/80 text-primary-foreground text-lg font-bold">
              {initials}
            </AvatarFallback>
          </Avatar>
        )}

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h4 className="font-semibold text-foreground group-hover:text-primary transition-colors truncate">
              {displayName}
            </h4>
            {profile.is_verified && (
              <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
            )}
          </div>
          
          <div className="flex items-center gap-3 text-sm text-muted-foreground">
            {showAsCompany && (
              <Badge variant="secondary" className="text-xs bg-primary/10 text-primary border-primary/20">
                <Building2 className="w-3 h-3 mr-1" />
                Company
              </Badge>
            )}
            {isDealer ? (
              <Badge variant="secondary" className="text-xs bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/20">
                <Building2 className="w-3 h-3 mr-1" />
                Verified Dealer
              </Badge>
            ) : !showAsCompany && (
              <span className="text-muted-foreground">Private Seller</span>
            )}
            
            {memberSince && (
              <span className="flex items-center gap-1 text-xs">
                <Calendar className="w-3 h-3" />
                Since {memberSince}
              </span>
            )}
          </div>
        </div>

        {/* Arrow */}
        <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all flex-shrink-0" />
      </div>
    </div>
  );
};

export default SellerSection;
